<script setup lang="ts">
import {inject, ref} from "vue";
import {useRouter} from "vue-router";
import {FilterMatchMode} from "@primevue/core/api";
import {getAiPrompts as getAiPromptsFromApi} from "@/Services/Admin/admin-ai-prompts";
import Str from "@/Utils/Str";
import {Button, Column, DataTable, MultiSelect} from "@/Components/Library";
import {PageHeading, TableRecordActions} from "@/Components/Global";
import markdownit from "markdown-it";
import type dayjs from "dayjs";
import type {AdminIndex} from "@/Types";

type AiPromptLocal = {
    id: string;
    title: string;
    content: string;
    category: {
        value: number;
        label: string;
        name: string;
    } | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    deleted_at: string;
};

const $date = inject("date") as typeof dayjs;
const router = useRouter();
const md = markdownit();

const aiPrompts = ref<AdminIndex<AiPromptLocal>>();

const filters = ref({
    roles: {
        value: [],
        matchMode: FilterMatchMode.IN,
    },
    is_active: {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
    },
});

const getAiPrompts = async () => {
    getAiPromptsFromApi({
        include: [],
        fields: [],
        ...router.currentRoute.value.query,
    }).then((data) => (aiPrompts.value = data));
};
getAiPrompts();
</script>
<template>
    <div>
        <div class="m-container">
            <PageHeading title="AI Prompts">
                <RouterLink class="ml-auto focus-visible:outline-none" :to="{name: 'admin.ai-prompts.create'}">
                    <Button
                        rounded
                        class="bg-askr-green text-white transition duration-200 hover:brightness-110"
                        icon="prime:plus"
                    >
                        Add AI Prompt
                    </Button>
                </RouterLink>
            </PageHeading>
        </div>
        <DataTable
            :data="aiPrompts"
            dataKey="id"
            :loading="false"
            :filters="filters"
            @filter="getAiPrompts"
            :allowTagSearch="false"
            defaultSort="-deleted_at,-created_at"
        >
            <Column expander style="width: 0" />
            <Column field="id" header="ID" />
            <Column field="title" header="Title" :sortable="true" />
            <Column field="content" header="Content" :sortable="false">
                <template #body="{data}">
                    {{ Str.of(data.content).truncate() }}
                </template>
            </Column>
            <Column field="category" header="Category" :sortable="false" class="w-36">
                <template #body="{data}">
                    {{ data.category?.label || "-" }}
                </template>
            </Column>
            <Column
                field="is_active"
                header="Status"
                :sortable="true"
                :showFilterMatchModes="false"
                filter-field="is_active"
                class="w-24"
            >
                <template #body="{data}">
                    <span
                        :class="data.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    >
                        {{ data.is_active ? "Active" : "Inactive" }}
                    </span>
                </template>
                <template #filter="{filterModel, filterCallback}">
                    <MultiSelect
                        v-model="filterModel.value"
                        @change="filterCallback"
                        :options="[true, false]"
                        placeholder="Select status"
                        class="p-column-filter"
                        style="min-width: 12rem"
                        :maxSelectedLabels="1"
                    >
                        <template #option="slotProps">
                            <div class="align-items-center flex">
                                <span>{{ slotProps.option ? "Active" : "Inactive" }}</span>
                            </div>
                        </template>
                        <template #value="slotProps">
                            <div v-if="slotProps.value && slotProps.value.length" class="flex items-center">
                                <span>{{ slotProps.value[0] ? "Active" : "Inactive" }}</span>
                            </div>
                            <span v-else>{{ slotProps.placeholder }}</span>
                        </template>
                    </MultiSelect>
                </template>
            </Column>
            <Column field="created_at" header="Created At" :sortable="true">
                <template #body="{data}">
                    {{ $date(data.created_at).format("DD/MM/YYYY HH:mm") }}
                </template>
            </Column>
            <Column field="updated_at" header="Updated At" :sortable="true">
                <template #body="{data}">
                    {{ $date(data.updated_at).format("DD/MM/YYYY HH:mm") }}
                </template>
            </Column>
            <Column field="deleted_at" header="Deleted At" :sortable="true" class="whitespace-nowrap">
                <template #body="{data}">
                    {{ data.deleted_at ? $date(data.deleted_at).format("DD/MM/YYYY HH:mm") : "-" }}
                </template>
            </Column>
            <Column header="Actions" style="width: 0">
                <template #body="{data}">
                    <div class="flex items-center gap-1">
                        <TableRecordActions
                            :name="data.title"
                            :deleted="!!data.deleted_at"
                            :routes="{
                                edit: {
                                    name: 'admin.ai-prompts.edit',
                                    params: {aiPrompt: data.id},
                                },
                                delete: route('admin.ai-prompts.destroy', data.id),
                                restore: route('admin.ai-prompts.restore', data.id),
                            }"
                            @deleted="getAiPrompts"
                        />
                    </div>
                </template>
            </Column>
            <template #expansion="{data}">
                <h2 class="mb-2 text-xl font-bold">{{ data.title }}</h2>
                <p class="mb-4">
                    <span class="font-semibold">Category:</span>
                    {{ data.category?.label || "Uncategorized" }}
                </p>
                <p class="mb-4">
                    <span class="font-semibold">Status:</span>
                    <span
                        :class="data.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    >
                        {{ data.is_active ? "Active" : "Inactive" }}
                    </span>
                </p>
                <div
                    class="prose prose-stone w-full max-w-full dark:prose-invert"
                    v-html="Str.of(md.render(data.content)).toString()"
                ></div>
            </template>
        </DataTable>
    </div>
</template>
<style scoped>
tr td {
    @apply py-1 pr-2;
}

tr td:first-child {
    @apply font-bold;
}
</style>
